import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';

const WebSocketContext = createContext(null);

export const WebSocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const listenersRef = React.useRef([]);
  const socketRef = React.useRef(null);
  const reconnectTimeoutRef = React.useRef(null);
  const retryCountRef = React.useRef(0);
  const isConnectingRef = React.useRef(false);

  const subscribe = useCallback((callback) => {
    listenersRef.current.push(callback);
    return () => {
      listenersRef.current = listenersRef.current.filter(l => l !== callback);
    };
  }, []);

  const connect = useCallback(() => {
    // Evita múltiplas tentativas simultâneas ou se já estiver conectado
    if (isConnectingRef.current || socketRef.current?.readyState === WebSocket.OPEN) return;

    isConnectingRef.current = true;
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(`${protocol}//${window.location.host}/ws`);

    ws.onopen = () => {
      setIsConnected(true);
      isConnectingRef.current = false;
      retryCountRef.current = 0; // Reseta tentativas no sucesso
      const timer = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send("ping");
      }, 25000);
      ws._heartbeat = timer;
    };

    ws.onmessage = (event) => {
      if (event.data === "pong") return;
      try {
        const data = JSON.parse(event.data);
        listenersRef.current.forEach(callback => callback(data));
      } catch (err) {}
    };

    ws.onclose = () => {
      setIsConnected(false);
      isConnectingRef.current = false;
      if (ws._heartbeat) clearInterval(ws._heartbeat);
      
      const delays = [1000, 2000, 5000];
      const delay = delays[retryCountRef.current] || 5000;
      retryCountRef.current = Math.min(retryCountRef.current + 1, delays.length - 1);
      
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = setTimeout(connect, delay);
    };

    ws.onerror = () => {
      ws.close(); // Fecha silenciosamente
    };

    socketRef.current = ws;
  }, []);

  useEffect(() => {
    connect();
    return () => {
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      const socket = socketRef.current;
      if (socket) {
        // Se estiver apenas conectando, não fechamos (para evitar erro no console)
        // apenas limpamos os eventos para que ele não tente reconectar ou avisar
        socket.onopen = null;
        socket.onmessage = null;
        socket.onerror = null;
        socket.onclose = null;
        
        if (socket.readyState === WebSocket.OPEN) {
          socket.close();
        }
      }
    };
  }, [connect]);

  return (
    <WebSocketContext.Provider value={{ isConnected, subscribe }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => useContext(WebSocketContext);
